import React, { useState, useEffect } from 'react';
import { Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Toast as ToastType } from '../types';
import { getBackgroundColor, getProgressColorHex, getIcon, getTextColor } from './toast-utils';
import classNames from '../../../../utils/class-names';

interface ToastProps {
  toast: ToastType;
  onRemove: (id: string) => void;
  duration: number;
}

const Toast: React.FC<ToastProps> = ({ toast, onRemove, duration }) => {
  const [isShowing, setIsShowing] = useState(true);

  const handleRemove = () => {
    setIsShowing(false);
  };

  const handleAfterLeave = () => {
    onRemove(toast.id);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsShowing(false);
    }, duration - 200);

    return () => clearTimeout(timeout);
  }, [duration]);

  return (
    <Transition
      appear={true}
      show={isShowing}
      as="div"
      enter="transform transition ease-out duration-300"
      enterFrom="translate-x-4 opacity-0"
      enterTo="translate-x-0 opacity-100"
      leave="transform transition ease-in duration-300"
      leaveFrom="translate-x-0 opacity-100"
      leaveTo="translate-x-4 opacity-0"
      afterLeave={handleAfterLeave}
    >
      <div
        className={classNames(
          'relative flex items-center min-w-[320px] max-w-[420px] p-3 pr-2',
          'rounded-md shadow-md hover:shadow-lg transition-shadow duration-200',
          'border border-b-0 border-gray-100 overflow-hidden',
          getBackgroundColor(toast.type)
        )}
      >
        <div
          className="absolute bottom-0 left-0 h-1 origin-left z-20"
          style={{
            backgroundColor: getProgressColorHex(toast.type),
            animation: `shrink ${duration}ms linear forwards`,
            width: '100%',
          }}
        />
        <div className="flex-shrink-0 mr-3 z-30">
          {getIcon(toast.type)}
        </div>
        <div
        data-cy={`halda-${toast.type.toLowerCase()}-toast-banner`}
          className={classNames(
            'flex-1 text-sm font-medium z-30',
            getTextColor(toast.type)
          )}
        >
          {toast.message}
        </div>
        <button
          onClick={handleRemove}
          className={classNames(
            'p-1 rounded-md bg-transparent',
            'outline-0 hover:outline-0 ring-0 hover:ring-0 border-0 hover:border-0 focus:outline-none'
          )}
          style={{ color: getProgressColorHex(toast.type) }}
        >
          <XMarkIcon className="size-5" />
        </button>
      </div>
    </Transition>
  );
};

export default Toast;
